import { useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'

import styles from '@/pages/app/Enterprises/components/BillingConfigs/BillingConfigTable/BillingConfigTable.module.scss'

import { isApiError } from '@/api'
import { EnterpriseBillingConfigResponse } from '@/api/generated'
import { deleteBillingConfig } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import { useToast } from '@/components/Toast/ToastContext'
import { captureEvent } from '@/utils/captureEvent'
import { formatDate, formatPricing } from '@/pages/app/Enterprises/components/BillingConfigs/utils'
import { EventName } from '@/utils/eventDefinitions'

interface BillingConfigTableProps {
  configs: EnterpriseBillingConfigResponse[]
  isLoading: boolean
  onEdit: (config: EnterpriseBillingConfigResponse) => void
  onCreate: () => void
}

const columnHelper = createColumnHelper<EnterpriseBillingConfigResponse>()

const BillingConfigTable = ({ configs, isLoading, onEdit, onCreate }: BillingConfigTableProps) => {
  const [configToDelete, setConfigToDelete] = useState<EnterpriseBillingConfigResponse | null>(null)
  const [sorting, setSorting] = useState<SortingState>([])
  const [search, setSearch] = useState('')
  const searchBarRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const { updateToast } = useToast()

  const deleteMutation = useMutation({
    mutationFn: (configId: string) => deleteBillingConfig(configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingConfigs'] })
      if (configToDelete) {
        captureEvent(EventName.BillingConfigDeleted, {
          config_id: configToDelete.id,
          enterprise_name: configToDelete.enterprise_name,
        })
      }
      setConfigToDelete(null)
    },
    onError: (err) => {
      console.error('Failed to delete billing config:', err)
      const message = isApiError(err) ? err.detail || 'Failed to delete billing config' : 'Failed to delete billing config'
      updateToast(message, 'error')
    },
  })

  const handleDelete = () => {
    if (!configToDelete) return
    deleteMutation.mutate(configToDelete.id)
  }

  const columns = [
    columnHelper.accessor('enterprise_name', {
      header: 'Enterprise',
      cell: (info) => <span className={styles.enterpriseName}>{info.getValue()}</span>,
      sortingFn: 'alphanumeric',
    }),
    columnHelper.display({
      id: 'pricing',
      header: 'Pricing',
      cell: (info) => formatPricing(info.row.original.pricing_tiers, info.row.original.custom_unit_price_cents),
    }),
    columnHelper.accessor('next_true_up_date', {
      header: 'Next True-up Date',
      cell: (info) => formatDate(info.getValue()),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.next_true_up_date ?? ''
        const b = rowB.original.next_true_up_date ?? ''
        return a.localeCompare(b)
      },
    }),
    columnHelper.accessor('contract_end_date', {
      header: 'Contract End Date',
      cell: (info) => formatDate(info.getValue()),
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const config = info.row.original
        return (
          <div className={styles.actionButtons}>
            <Button type="dark" onClick={() => onEdit(config)} title="Edit" />
            <Button type="destructiveLight" onClick={() => setConfigToDelete(config)} title="Delete" />
          </div>
        )
      },
    }),
  ]

  const filteredConfigs = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return configs
    return configs.filter((config) => config.enterprise_name.toLowerCase().includes(query))
  }, [configs, search])

  const clearSearch = () => {
    setSearch('')
    searchBarRef.current?.querySelector('input')?.focus()
  }

  const table = useReactTable({
    data: filteredConfigs,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.loadingState}>Loading billing configs...</div>
      </div>
    )
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableControls}>
        <div className={styles.tableTitleSection}>
          <span className={styles.tableTitle}>Custom Billing Configs</span>
        </div>
        <div className={styles.filters}>
          {configs.length > 0 && (
            <div
              ref={searchBarRef}
              className={styles.searchBar}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && search) clearSearch()
              }}
            >
              <Search className={styles.searchIcon} aria-hidden="true" />
              <Input
                type="text"
                value={search}
                onInput={setSearch}
                placeholder="Search enterprise"
                ariaLabel="Search billing configs by enterprise"
                className={styles.searchInput}
              />
              {search && (
                <button type="button" className={styles.clearSearch} onClick={clearSearch} aria-label="Clear search">
                  <X aria-hidden="true" />
                </button>
              )}
            </div>
          )}
          <Button title="+ Add" onClick={onCreate} type="dark" />
        </div>
      </div>

      {configs.length === 0 ? (
        <div className={styles.emptyState}>No billing configs yet. Click &apos;+ Add&apos; to create one.</div>
      ) : filteredConfigs.length === 0 ? (
        <div className={styles.emptyState}>No billing configs match &quot;{search.trim()}&quot;.</div>
      ) : (
        <table className={styles.table} aria-label="Billing configurations">
          <thead className={styles.tableHeader}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={header.column.getCanSort() ? styles.sortableHeader : undefined}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted() as string] ?? ''}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={styles.tableBody}>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <DialogWrapper isOpen={!!configToDelete} title="Delete Billing Config" close={() => setConfigToDelete(null)} minWidth={400}>
        <div className={styles.dialogBody}>
          <p>Are you sure you want to delete this billing config for {configToDelete?.enterprise_name}?</p>
          <div className={styles.dialogFooter}>
            <Button title="Cancel" onClick={() => setConfigToDelete(null)} type="light" />
            <Button
              title={deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              onClick={handleDelete}
              type="destructive"
              isDisabled={deleteMutation.isPending}
            />
          </div>
        </div>
      </DialogWrapper>
    </div>
  )
}

export default BillingConfigTable
